import {
  useState,
  useEffect,
  ChangeEvent,
  DragEvent,
  ReactElement,
} from "react";
import { DownloadIcon } from "@twilio-paste/icons/esm/DownloadIcon";
import { InformationIcon } from "@twilio-paste/icons/esm/InformationIcon";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Tooltip } from "@twilio-paste/core/tooltip";
import { ScreenReaderOnly } from "@twilio-paste/core/screen-reader-only";
import {
  FileUploader,
  FileUploaderDropzone,
  FileUploaderDropzoneText,
  FileUploaderErrorText,
  FileUploaderHelpText,
  FileUploaderItem,
  FileUploaderItemDescription,
  FileUploaderItemsList,
  FileUploaderItemTitle,
  FileUploaderLabel,
} from "@twilio-paste/core/file-uploader";

import { constants, copy, types } from "./definitions";
import { formatFile } from "./utils";
import styles from "./CompositeFileUploader.module.css";

const CompositeFileUploader = ({
  acceptedFileTypes = constants.defaultAcceptedFileTypes,
  disabled = false,
  // Default from Paste component
  // Note: Minimum height is 7.5rem; any lower will break Paste UI mesausrements
  dropZoneHeight = "12rem",
  files,
  helpText = "",
  id,
  label = copy.label,
  // Paste recommendation: https://paste.twilio.design/components/textarea/#positioning-form-fields
  marginBottom = "space80",
  maxFileSize = constants.DEFAULT_MAX_FILE_SIZE,
  name,
  onChange,
  onRemove,
  required = false,
  width,
}: types.CompositeFileUploaderProps): ReactElement => {
  const [invalidFiles, updateInvalidFiles] = useState<File[]>([]);
  const [screenReaderText, setScreenReaderText] = useState("");
  const [renderedFiles, updateRenderedFiles] = useState<
    types.CompositeFileUploaderFile[]
  >([]);

  const hasError = !!invalidFiles.length;

  useEffect(() => {
    if (files) {
      const { formattedFiles } = handleFormatFiles(files);
      updateRenderedFiles(formattedFiles);
    }
  }, [files]);

  const handleFormatFiles = (
    filesToFormat: File[]
  ): types.HandleFormatFilesReturn => {
    // Screen Reader text placeholder
    let newFilesNames = "";
    let hasInvalidFile = !!invalidFiles.length;

    const formattedFiles = filesToFormat.map((file) => {
      newFilesNames = `${newFilesNames} ${file.name}`;
      const { isValid, formattedFile } = formatFile({ file, maxFileSize });

      if (!isValid) {
        hasInvalidFile = true;
        updateInvalidFiles([...invalidFiles, file]);
      }

      return formattedFile;
    });

    setScreenReaderText(copy.screenReader.fileUploadComplete(newFilesNames));

    return {
      hasInvalidFile,
      formattedFiles,
    };
  };

  const handleDragEnter = (event: DragEvent<HTMLLabelElement>): void => {
    const { items } = event.dataTransfer;
    setScreenReaderText(copy.screenReader.fileDragEnter(items.length));
  };

  const handleDragLeave = (event: DragEvent<HTMLLabelElement>): void => {
    const { items } = event.dataTransfer;
    setScreenReaderText(copy.screenReader.fileDragLeave(items.length));
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>): void => {
    const { files: fileList } = event.dataTransfer;

    if (fileList) {
      // Spread files to convert from type FileList to File[]
      const fileListArray = Array.from(fileList);
      const newFiles = [...fileListArray] as File[];

      setScreenReaderText(copy.screenReader.fileDrop(newFiles.length));

      const { hasInvalidFile, formattedFiles } = handleFormatFiles(newFiles);

      updateRenderedFiles([...renderedFiles, ...formattedFiles]);

      onChange(newFiles, hasInvalidFile);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { files: fileList } = event.target;

    if (fileList) {
      // Spread files to convert from type FileList to File[]
      const fileListArray = Array.from(fileList);
      const newFiles = [...fileListArray] as File[];

      const { hasInvalidFile, formattedFiles } = handleFormatFiles(newFiles);

      updateRenderedFiles([...renderedFiles, ...formattedFiles]);

      onChange(newFiles, hasInvalidFile);
    }
  };

  const handleRemoveFile = (
    fileToDelete: types.CompositeFileUploaderFile
  ): void => {
    let hasInvalidFile = !!invalidFiles.length;

    const newRenderedFiles = renderedFiles.filter(
      (file) => file.title !== fileToDelete.title
    );

    if (fileToDelete.variant === "error") {
      const newInvalidFiles = invalidFiles.filter(
        (file) => file.name !== fileToDelete.file.name
      );

      hasInvalidFile = !!newInvalidFiles.length;
      updateInvalidFiles(newInvalidFiles);
    }

    onRemove(fileToDelete.file, hasInvalidFile);
    updateRenderedFiles(newRenderedFiles);
  };

  const renderFiles = (): ReactElement[] =>
    renderedFiles.map((file) => (
      <FileUploaderItem
        key={file.title}
        variant={file.variant}
        fileIcon={<DownloadIcon decorative />}
        onButtonClick={(): void => handleRemoveFile(file)}
      >
        <FileUploaderItemTitle>{file.title}</FileUploaderItemTitle>
        <FileUploaderItemDescription>
          {file.description}
        </FileUploaderItemDescription>
      </FileUploaderItem>
    ));

  const renderHelpText = (): ReactElement => {
    const shouldRenderDefaultHelpText =
      !helpText && maxFileSize && acceptedFileTypes;

    return shouldRenderDefaultHelpText ? (
      <Box as="span" className={styles.helpTextContainer}>
        {copy.helpText(maxFileSize)}

        <Tooltip
          text={copy.toolTip.text(acceptedFileTypes)}
          placement="bottom-start"
        >
          <Button variant="secondary_icon">
            <InformationIcon decorative />
          </Button>
        </Tooltip>
      </Box>
    ) : (
      <>{helpText}</>
    );
  };

  return (
    <Box
      as="div"
      className={styles.compositeFileUploaderContainer}
      marginBottom={marginBottom}
      width={width}
    >
      <FileUploader
        id={id}
        name={name}
        required={required}
        disabled={disabled}
        aria-disabled={disabled}
      >
        <FileUploaderLabel>{label}</FileUploaderLabel>
        <FileUploaderHelpText>{renderHelpText()}</FileUploaderHelpText>

        <Box
          as="div"
          className={styles.dropZoneContainer}
          style={{ height: dropZoneHeight }}
        >
          <FileUploaderDropzone
            multiple
            acceptedMimeTypes={acceptedFileTypes}
            onDragEnter={handleDragEnter}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            onInputChange={handleInputChange}
          >
            <FileUploaderDropzoneText>{copy.dropZone}</FileUploaderDropzoneText>
          </FileUploaderDropzone>
        </Box>

        {hasError && (
          <FileUploaderErrorText>
            {copy.errors.invalidFile}
          </FileUploaderErrorText>
        )}

        <Box as="div" className={styles.fileUploadListContainer}>
          <FileUploaderItemsList>{renderFiles()}</FileUploaderItemsList>
        </Box>
      </FileUploader>

      <ScreenReaderOnly aria-live="assertive">
        {screenReaderText}
      </ScreenReaderOnly>
    </Box>
  );
};

export default CompositeFileUploader;
